// ═══════════════════════════════════════════════════════════════
// TaxPicker — "what tax am I charging on this one?"
//
// Two doors, and which one you get depends on whether you've been
// here before:
//
//   Saved rates → pick one. One tap, done.
//   Nothing saved yet → type the two numbers.
//
// A first-time user never sees an empty list with a "you have no
// presets" message, because an empty list is not a choice — it's a
// dead end that makes you go to Settings and come back. So with
// nothing saved, this opens straight on the number pad and offers to
// remember the rate afterwards. The preset list builds itself out of
// real jobs instead of being something you have to go and populate
// before the feature works.
// ═══════════════════════════════════════════════════════════════
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Percent, Bookmark, ChevronRight } from 'lucide-react';
import {
  normalizeRate, taxPresetSummary, MAX_TAX_RATE,
} from './lib/tax-presets.js';
import { computeJobTax, formatPrice } from './lib/job-helpers.js';

export default function TaxPicker({
  open,
  ...props
}) {
  // Mount only while open: every visit starts from the ticket's current tax.
  // Keep this form outside the ticket form so Apply cannot save the ticket.
  return open ? createPortal(<TaxPickerDialog {...props} />, document.body) : null;
}

function TaxPickerDialog({
  presets = [],
  lineItems = [],
  shopSupplies = 0,
  current = null,
  onApply,
  onSavePreset,
  onClose,
}) {
  const usable = Array.isArray(presets) ? presets.filter((p) => p?.label) : [];

  // Start on the list only when there's a list worth showing AND the
  // job doesn't already have a rate on it. Editing an existing rate
  // goes straight to the numbers — you came here to change them.
  const [mode, setMode] = useState(
    usable.length > 0 && !current ? 'choose' : 'manual'
  );
  const [label, setLabel] = useState(current?.label || '');
  const [partsRate, setPartsRate] = useState(current?.partsRate ?? 0);
  const [laborRate, setLaborRate] = useState(current?.laborRate ?? 0);
  const [taxSupplies, setTaxSupplies] = useState(current?.taxSupplies !== false);
  const [remember, setRemember] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  function applyPreset(p) {
    onApply({
      label: p.label,
      partsRate: p.partsRate,
      laborRate: p.laborRate,
      taxSupplies: p.taxSupplies !== false,
      sourcePresetId: p.id || '',
    });
    onClose();
  }

  async function applyManual(e) {
    e.preventDefault();
    // Portal events still bubble through the React tree to the ticket form.
    e.stopPropagation();
    if (saving) return;
    const applied = {
      label: label.trim(),
      partsRate: normalizeRate(partsRate),
      laborRate: normalizeRate(laborRate),
      taxSupplies,
      sourcePresetId: current?.sourcePresetId || '',
    };
    // Saving a preset needs a name to find it by later. Without one
    // the rate still applies to this job — it just isn't remembered.
    onApply(applied);
    if (remember && applied.label && onSavePreset) {
      setSaving(true);
      setError(null);
      try {
        await onSavePreset({
          label: applied.label,
          partsRate: applied.partsRate,
          laborRate: applied.laborRate,
          taxSupplies: applied.taxSupplies,
        });
      } catch (err) {
        if (!mounted.current) return;
        setError('Tax is applied to this ticket, but the saved rate failed. Try again or uncheck “Save for next time”, then Apply.');
        setSaving(false);
        return;
      }
    }
    if (mounted.current) onClose();
  }

  // Live preview against this job's real numbers. A percentage is
  // abstract; "$6.05 on this job" is the thing the mechanic is
  // actually deciding about, and it catches a decimal in the wrong
  // place before the customer sees it.
  const preview = computeJobTax({
    lineItems,
    shopSupplies,
    tax: { partsRate, laborRate, taxSupplies },
  });

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        e.stopPropagation();
        if (!saving && e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box tax-picker" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2>
            <Percent size={17} style={{ verticalAlign: '-3px', marginRight: 6 }} />
            {mode === 'choose' ? 'Which tax rate?' : 'Tax rates'}
          </h2>
          <button type="button" className="btn-link" onClick={onClose} disabled={saving} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {mode === 'choose' ? (
          <>
            <p className="muted small tax-picker-help">
              Saved rates. Tap one, or enter different numbers for this job.
            </p>
            <div className="tax-preset-choices">
              {usable.map((p) => (
                <button
                  key={p.id || p.label}
                  type="button"
                  className="tax-preset-choice"
                  onClick={() => applyPreset(p)}
                >
                  <span className="tax-preset-choice-main">
                    <span className="tax-preset-choice-label">{p.label}</span>
                    <span className="tax-preset-choice-sub muted small">
                      {taxPresetSummary(p)}
                      {p.taxSupplies === false ? ' · supplies exempt' : ''}
                    </span>
                  </span>
                  <ChevronRight size={16} className="tax-preset-choice-go" />
                </button>
              ))}
            </div>
            <div className="form-actions tax-picker-actions">
              <button
                type="button"
                className="btn-link"
                onClick={() => setMode('manual')}
              >
                Enter rates by hand instead
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={applyManual}>
            <fieldset disabled={saving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            <p className="muted small tax-picker-help">
              Parts and labor are taxed separately because many states tax
              one and not the other. Leave a rate at 0 if it isn't taxed.
            </p>

            <label className="field">
              <span className="field-label">Name this rate (optional)</span>
              <input
                type="text"
                maxLength={60}
                placeholder="e.g. Lancaster County, or Out of town"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </label>

            <div className="tax-rate-row">
              <label className="field">
                <span className="field-label">Parts</span>
                <div className="tax-rate-input">
                  <input
                    type="number"
                    min={0}
                    max={MAX_TAX_RATE}
                    step="0.001"
                    inputMode="decimal"
                    placeholder="0"
                    value={partsRate === 0 ? '' : partsRate}
                    onChange={(e) => setPartsRate(e.target.value)}
                  />
                  <span className="tax-rate-pct">%</span>
                </div>
              </label>

              <label className="field">
                <span className="field-label">Labor</span>
                <div className="tax-rate-input">
                  <input
                    type="number"
                    min={0}
                    max={MAX_TAX_RATE}
                    step="0.001"
                    inputMode="decimal"
                    placeholder="0"
                    value={laborRate === 0 ? '' : laborRate}
                    onChange={(e) => setLaborRate(e.target.value)}
                  />
                  <span className="tax-rate-pct">%</span>
                </div>
              </label>
            </div>

            <label className="tax-check-row">
              <input
                type="checkbox"
                checked={taxSupplies}
                onChange={(e) => setTaxSupplies(e.target.checked)}
              />
              <span>
                Tax shop supplies at the parts rate
                <span className="muted small"> — most states treat them as goods</span>
              </span>
            </label>

            {preview.total > 0 && (
              <div className="tax-preview">
                <span className="muted small">Tax on this job</span>
                <span className="mono tax-preview-amount">{formatPrice(preview.total)}</span>
              </div>
            )}

            {label.trim() && onSavePreset && (
              <label className="tax-check-row">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <span>
                  <Bookmark size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                  Save “{label.trim()}” for next time
                </span>
              </label>
            )}

            {error && <p className="form-error" role="alert">{error}</p>}
            <p className="muted small">Apply updates this ticket’s tax. Save the ticket to keep your changes.</p>
            <div className="form-actions">
              {usable.length > 0 && (
                <button
                  type="button"
                  className="btn-link"
                  onClick={() => setMode('choose')}
                >
                  Back to saved rates
                </button>
              )}
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn">{saving ? 'Saving rate…' : 'Apply'}</button>
            </div>
            </fieldset>
            {saving && (
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={onClose}>
                  Keep tax and return to ticket
                </button>
                <p className="muted small">The saved rate can finish syncing in the background.</p>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
